import { useEffect, useRef, useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { ToastProvider } from '@/components/ui';
import { CookieConsent } from '@/components/consent';
import { ThemeProvider } from '@/lib/theme/theme-context';
import { BlogAuthProvider, useBlogAuth } from '../lib/auth';
import { BlogApiProvider, isBlogApiError } from '../lib/api';
import { SiteConfigProvider } from '../lib/site';
import { blogRouter } from './router';

/**
 * O QueryClient do portal, uma vez por instância. Falhas de auth/permissão não
 * tentam de novo (não se curam sozinhas); erros transitórios têm duas chances.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (
            isBlogApiError(error) &&
            (error.isUnauthorized || error.isForbidden || error.isNotFound)
          ) {
            return false;
          }
          return failureCount < 2;
        },
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * Esvazia o cache de queries quando a sessão TERMINA ou é TROCADA (outra conta
 * entrando na mesma aba): entitlements e compras são do membro, e um cache que
 * sobrevivesse à troca serviria ao segundo os dados do primeiro. O PRIMEIRO
 * login da aba fica de fora de propósito: o cache está vazio, e limpá-lo
 * derrubaria as leituras públicas que a vitrine acabou de começar.
 */
function SessionScopedCache({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useBlogAuth();
  const queryClient = useQueryClient();
  const identity = user ? user.id : null;
  const previous = useRef(identity);

  useEffect(() => {
    if (previous.current === identity) return;
    if (previous.current !== null) queryClient.clear();
    previous.current = identity;
  }, [identity, queryClient]);

  return <>{children}</>;
}

/**
 * Raiz de composição do portal Se Esforce (blog + área de membros). A ordem dos
 * providers importa: tema primeiro (pinta os tokens), depois o cache, a sessão
 * e o reset do cache quando ela termina, o client da Members API ligado à
 * sessão, a configuração do site (que cai nos defaults sem a API), os toasts
 * e, por fim, o router. O consentimento de cookies fica ao
 * lado do router (vale em toda rota, política inclusa) e dentro da sessão, que
 * decide se há anúncios em jogo para este leitor.
 */
export function BlogApp(): JSX.Element {
  const [queryClient] = useState(createQueryClient);

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BlogAuthProvider>
          <SessionScopedCache>
            <BlogApiProvider>
              <SiteConfigProvider>
                <ToastProvider>
                  <RouterProvider router={blogRouter} />
                  <CookieConsent />
                </ToastProvider>
              </SiteConfigProvider>
            </BlogApiProvider>
          </SessionScopedCache>
        </BlogAuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
